import { useEffect, useState } from "react"

// ** Third Party Components
import axios from "axios"
import * as XLSX from "xlsx"
import { parseCookies } from "nookies"
import { useNavigate } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertTriangle, ChevronsUpDown, Download, FileSpreadsheet } from "lucide-react"

// ** UI Components
import { Label } from "@components/common/label"
import { Alert } from "@components/common/alert"
import { Button } from "@components/common/button"
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from "@components/common/popover"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/common/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/common/table"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@components/common/command"

// ** API-Routes
import { ESTIMATE_BOQ_ENDPOINT, FINANCIAL_SERVER_URL } from "@pma/utils/api-routes"

// ** Types
import { DictType } from "@pma/types"

// ** Utils
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { cn, getFinancialErrorMessage } from "@pma/utils/helpers"
import { getFilteredProjectsByCurrentUser } from "@pma/utils/utils"
import { FINANCIAL_BOQ_ROUTE, PROJECTS_PAGE_ROUTE } from "@pma/utils/page-routes"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

// ** Custom Components
import { PriceListExcelMapper } from "./excel-mapper"
interface ValidationError {
  row: number
  column: string
  message: string
}

interface Props {
  projectId?: string
  defaultOpen?: boolean
  setDefaultOpen?: CallableFunction
  downloadTpl?: boolean
  revalidate?: CallableFunction
}
const CreateNewBoQEstimate = ({
  projectId,
  defaultOpen,
  setDefaultOpen,
  downloadTpl,
  revalidate,
}: Props) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { translate, locale } = useTranslator()
  const { authConfig, setProjectFinancial } = useAuth()

  const isHebrew = locale === "he"
  const orgId = authConfig?.last_organization_id
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const [showUploadDialog, setShowUploadDialog] = useState(false)
  const [showRolesWarning, setShowRolesWarning] = useState(false)
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([])
  const [stateProjectId, setStateProjectId] = useState(projectId ? projectId : undefined)
  const [fileInformation, setFileInformation] = useState<DictType>({
    Name: "",
    Number: "",
    Unit: "",
    Price: "",
    Description: "",
    File: "",
  })

  const { data: projects } = useQuery({
    queryKey: ["projects"],
    queryFn: () =>
      getFilteredProjectsByCurrentUser({
        by_organization_id: orgId,
        status_id: 0,
        paging_per_page: 30,
      }),
  })

  const addBoQEstimate = async () => {
    const excelFile = fileInformation?.File
    setShowRolesWarning(false)
    const response = await axios.post(
      FINANCIAL_SERVER_URL + ESTIMATE_BOQ_ENDPOINT,
      {
        colunm_name_number: fileInformation.Number || undefined,
        colum_name_unit: fileInformation?.Unit || undefined,
        colum_name_price: fileInformation?.Price || undefined,
        column_name_description: fileInformation?.Description || undefined,
        boq_name: fileInformation?.Name || undefined,
        column_name_amount: fileInformation?.Amount || undefined,
        project_id: stateProjectId,
        xls_file: excelFile[0],
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "multipart/form-data",
        },
      },
    )

    return response.data
  }

  const addBoQMutation = useMutation({
    mutationKey: ["boq-estimate"],
    mutationFn: addBoQEstimate,
    onSuccess: (data) => {
      toast({
        title: translate("Success"),
        description: translate("BOQ added successfully"),
        variant: "success",
      })

      setProjectFinancial({
        projectId: data?.project_id,
        estimate: data?.id,
        main: undefined,
      })

      revalidate && revalidate()

      queryClient.invalidateQueries({ queryKey: ["estimate-boq"] })

      setShowUploadDialog(false)
      setFileInformation({})
      setShowRolesWarning(false)
      setStateProjectId(undefined)

      if (projectId) {
        return navigate(PROJECTS_PAGE_ROUTE + `/${projectId}?estimate=${data?.id}`)
      }
      if (defaultOpen) {
        closeDialog()
      } else {
        navigate(FINANCIAL_BOQ_ROUTE + `/${data?.id}`)
      }
    },
    onError: (error: DictType) => {
      const errorResponse = error?.response?.data
      if (errorResponse?.code === "ERROR_CODE_ACCESS_DENIED") {
        setShowRolesWarning(true)
      }

      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "create estimate", translate),
        variant: "error",
      })
    },
  })

  const closeDialog = () => {
    setDefaultOpen && setDefaultOpen(false)
    setStateProjectId(projectId ? projectId : undefined)
    setShowUploadDialog(false)
    setValidationErrors([])
    return
  }

  const downloadTemplate = (language: string) => {
    const templateUrl = `${process.env.PUBLIC_URL}/assets/boq-template-${language}.xlsx`
    const link = document.createElement("a")
    link.href = templateUrl
    link.setAttribute("download", `boq-template-${language}.xlsx`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const selectedProject = projects?.items?.find((project: DictType) => project.id == stateProjectId)

  useEffect(() => {
    if (defaultOpen) {
      setShowUploadDialog(true)
    }
  }, [])

  useEffect(() => {
    setValidationErrors([])
  }, [fileInformation?.File])

  // Helper function to check if row is empty
  const isRowEmpty = (row: any[]): boolean => {
    return row.every((cell) => {
      if (cell === null || cell === undefined) return true
      if (typeof cell === "string" && cell.trim() === "") return true
      return false
    })
  }

  // Helper function to validate numeric values
  const isNumeric = (val: any): boolean => {
    if (val === "" || val === null || val === undefined) return true
    const strVal = String(val).trim()
    return /^-?[0-9]+(\.[0-9]+)?$/.test(strVal)
  }

  // Process SKN file and convert to CSV
  const processSknFile = async (file: File): Promise<File> => {
    const formData = new FormData()
    formData.append("file", file)

    const response = await axios.post(
      "https://xk6s-eqwg-s8by.f2.xano.io/api:0Dvy81hK:v2/service/skn-to-csv",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    if (!response.data?.success || !response.data?.csv) {
      throw new Error("Failed to convert SKN file to CSV")
    }

    const csvData = response.data.csv
    const blob = new Blob([csvData], { type: "text/csv;charset=windows-1255" })
    return new File([blob], `${file.name}_processed.csv`, { type: "text/csv" })
  }

  const validateExcelData = (
    filteredDataWithIndices: { row: any[]; originalIndex: number }[],
    columnIndices: Record<string, number>,
    columnNames: Record<string, string>,
    isSkn: boolean,
  ): ValidationError[] => {
    if (isSkn) return []

    const errors: ValidationError[] = []
    const decimalRegex = /^[0-9.]*$/
    const dataRowsWithIndices = filteredDataWithIndices.slice(1)

    dataRowsWithIndices.forEach(({ row, originalIndex }) => {
      const excelRowNumber = originalIndex + 1
      const numberVal = row[columnIndices.number]
      const amt = row[columnIndices.amount]
      const cost = row[columnIndices.price]

      if (numberVal && !decimalRegex.test(String(numberVal))) {
        errors.push({
          row: excelRowNumber,
          column: columnNames.number,
          message: `${translate("Invalid section/item number")}: "${numberVal}". ${translate("Only digits and dots allowed")}.`,
        })
      }

      if (!isNumeric(amt)) {
        errors.push({
          row: excelRowNumber,
          column: columnNames.amount,
          message: `${translate("Invalid amount")}: "${amt}". ${translate("Must be integer or decimal")}.`,
        })
      }

      if (!isNumeric(cost)) {
        errors.push({
          row: excelRowNumber,
          column: columnNames.price,
          message: `${translate("Invalid cost")}: "${cost}". ${translate("Must be integer or decimal")}.`,
        })
      }
    })

    return errors
  }

  const processExcelFile = (file: File, isSkn: boolean): Promise<ValidationError[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onload = (evt) => {
        try {
          const bstr = evt.target?.result
          const workbook = XLSX.read(bstr, { type: "array" })
          const ws = workbook.Sheets[workbook.SheetNames[0]]

          const rawData: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" })

          if (rawData.length === 0) {
            throw new Error("Excel file is empty")
          }

          const filteredDataWithIndices: { row: any[]; originalIndex: number }[] = []
          rawData.forEach((row, index) => {
            if (!isRowEmpty(row)) {
              filteredDataWithIndices.push({ row, originalIndex: index })
            }
          })

          if (filteredDataWithIndices.length === 0) {
            throw new Error("Excel file is empty")
          }

          const requiredHeaders = {
            number: fileInformation.Number,
            unit: fileInformation.Unit,
            price: fileInformation.Price,
            description: fileInformation.Description,
            amount: fileInformation.Amount,
          }

          let headerRowFilteredIndex = -1
          let headerRow: string[] = []

          for (let i = 0; i <= 1 && i < filteredDataWithIndices.length; i++) {
            const potentialHeader = filteredDataWithIndices[i].row.map((cell: any) =>
              String(cell).trim(),
            )
            const missingHeaders = Object.values(requiredHeaders).filter(
              (h) => !potentialHeader.includes(h),
            )

            if (missingHeaders.length === 0) {
              headerRowFilteredIndex = i
              headerRow = potentialHeader
              break
            }
          }

          const columnIndices = {
            number: headerRow.indexOf(requiredHeaders.number),
            unit: headerRow.indexOf(requiredHeaders.unit),
            price: headerRow.indexOf(requiredHeaders.price),
            description: headerRow.indexOf(requiredHeaders.description),
            amount: headerRow.indexOf(requiredHeaders.amount),
          }

          const dataRowsWithIndices = filteredDataWithIndices.slice(headerRowFilteredIndex + 1)
          if (dataRowsWithIndices.length === 0) {
            throw new Error("Excel file has no data rows")
          }

          const errors = validateExcelData(
            filteredDataWithIndices.slice(headerRowFilteredIndex),
            columnIndices,
            requiredHeaders,
            isSkn,
          )

          resolve(errors)
        } catch (error) {
          reject(error)
        }
      }

      reader.onerror = () => reject(new Error("Failed to read file"))
      reader.readAsArrayBuffer(file)
    })
  }

  // Show error toast
  const showErrorToast = (error: any) => {
    toast({
      title: translate("Error"),
      description: getFinancialErrorMessage(error, "create estimate", translate),
      variant: "error",
    })
  }

  const handleUploadFile = async () => {
    try {
      setValidationErrors([])
      const tmpFile = fileInformation?.File[0]
      if (!tmpFile) return

      const isSkn = tmpFile.name?.toLowerCase()?.endsWith(".skn")
      let processedFile: File

      if (isSkn) {
        processedFile = await processSknFile(tmpFile)
      } else {
        processedFile = tmpFile
      }

      const errors = await processExcelFile(processedFile, isSkn)

      if (errors.length > 0) {
        setValidationErrors(errors)
        return
      }

      addBoQMutation.mutate()
    } catch (error: any) {
      showErrorToast(error)
    }
  }

  return (
    <Dialog open={showUploadDialog} onOpenChange={closeDialog}>
      <div className='flex gap-2'>
        <Button
          onClick={() => setShowUploadDialog(true)}
          size={"sm"}
          className='text-xs'
          variant={"outline"}
        >
          {translate("Create Estimate")}
        </Button>

        {downloadTpl && (
          <Button
            onClick={() => downloadTemplate(locale)}
            size={"sm"}
            className='gap-2 text-xs'
            variant={"secondary"}
          >
            <Download className='h-4 w-4' />
            {translate("Download Template")}
          </Button>
        )}
      </div>

      <DialogContent
        className={cn(
          "flex max-h-svh w-full flex-col gap-2 overflow-auto md:max-w-2xl lg:max-w-4xl xl:max-w-7xl",
          isHebrew ? "rtl" : "ltr",
        )}
      >
        <DialogHeader className='flex flex-row items-center justify-between'>
          <div>
            <DialogTitle className='flex items-center gap-2'>
              <FileSpreadsheet className='h-5 w-5 text-primary' />
              {translate("Add new BoQ Estimate")}
            </DialogTitle>

            {defaultOpen && (
              <DialogDescription className='my-2 h-fit'>
                <Alert variant={"info"}>
                  {translate(
                    "Create a new BoQ Estimate in order to generate a new main BoQ for the project",
                  )}
                  .
                </Alert>
              </DialogDescription>
            )}
          </div>
        </DialogHeader>
        <div className='flex flex-col'>
          {!projectId && (
            <div className='flex flex-col gap-1.5 capitalize'>
              <Label>
                {translate("Project")}
                <strong className='text-destructive'>*</strong>
              </Label>
              <Popover>
                <PopoverTrigger
                  disabled={addBoQMutation.isPending}
                  className='flex items-center justify-between rounded-md border p-1.5'
                >
                  <div className='text-sm'>
                    {stateProjectId ? (
                      <div className='w-[23ch] overflow-hidden truncate text-ellipsis p-0.5 text-left text-xs font-semibold capitalize'>
                        {selectedProject?.title}
                      </div>
                    ) : (
                      translate("Select Project")
                    )}
                  </div>
                  <ChevronsUpDown size={12} />
                </PopoverTrigger>

                <PopoverContent className='p-0'>
                  <Command>
                    <CommandInput placeholder={translate("Search for project") + "..."} />
                    <CommandList>
                      <CommandEmpty>{translate("No projects found")}.</CommandEmpty>
                      <CommandGroup>
                        {projects?.items?.map((project: DictType) => (
                          <CommandItem
                            key={project.id}
                            value={project?.title}
                            defaultValue={project?.title}
                            onSelect={() => {
                              setStateProjectId(project?.id)
                            }}
                            className={cn(
                              "my-1 capitalize",
                              stateProjectId === project.id && "bg-secondary/30 text-black",
                            )}
                          >
                            <PopoverClose
                              className={cn("w-full", isHebrew ? "text-right" : "text-left")}
                            >
                              {project?.title}
                            </PopoverClose>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}

          <PriceListExcelMapper setMapping={setFileInformation} mappingType='boq' />

          {validationErrors.length > 0 && (
            <div className='mt-3 flex flex-col gap-2'>
              <Alert
                variant='destructive'
                className='flex items-start gap-2 border-destructive/40 bg-destructive/5'
              >
                <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-destructive' />
                <div className='text-sm'>
                  <p className='font-semibold'>
                    {translate("Validation failed")} ({validationErrors.length})
                  </p>
                  <p>
                    {translate(
                      "Fix the following issues in your Excel file and re-upload to continue",
                    )}
                    .
                  </p>
                </div>
              </Alert>
              <div className='max-h-[35vh] overflow-auto rounded-md border'>
                <Table className='text-sm'>
                  <TableHeader className='sticky top-0 bg-destructive/10'>
                    <TableRow>
                      <TableHead className={cn('w-20 whitespace-nowrap', isHebrew ? "text-right" : "text-left")}>{translate("Row")}</TableHead>
                      <TableHead className={cn('w-48 whitespace-nowrap', isHebrew ? "text-right" : "text-left")}>
                        {translate("Column")}
                      </TableHead>
                      <TableHead className={isHebrew ? "text-right" : "text-left"}>{translate("Error")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {validationErrors.map((err, i) => (
                      <TableRow key={`${err.row}-${err.column}-${i}`}>
                        <TableCell className='font-semibold'>{err.row}</TableCell>
                        <TableCell className='capitalize'>{err.column}</TableCell>
                        <TableCell className='text-destructive'>{err.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {fileInformation?.File && (
            <DialogFooter className='gap-2 pt-3'>
              <Button
                type='button'
                variant='outline'
                size={"sm"}
                className='min-w-[100px]'
                onClick={() => {
                  setStateProjectId(undefined)
                  setFileInformation({})
                  closeDialog()
                }}
              >
                {translate("Cancel")}
              </Button>
              <Button
                type='submit'
                size={"sm"}
                className='min-w-[100px]'
                disabled={!fileInformation?.File || !stateProjectId}
                loading={addBoQMutation.isPending && !addBoQMutation.isError}
                onClick={handleUploadFile}
              >
                {addBoQMutation.isPending && !addBoQMutation.isError
                  ? translate("Uploading")
                  : translate("Upload")}
              </Button>
            </DialogFooter>
          )}

          {showRolesWarning && (
            <Alert variant={"destructive"}>
              {translate(
                "Check project financial's permissions or contact workspace administrator",
              )}
              .
            </Alert>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default CreateNewBoQEstimate
